import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import PulseLoader from '@/components/PulseLoader';
import { authService } from '../../../../utils/authService';
import { fetchBatchesByCourseSingle } from '../../../../utils/batchUtils';
import { Session } from './sessionTypes';
import { asIdList, isOptionalSession } from './sessionUtils';

type InvitedStudent = {
  student_id: string;
  name: string;
  email?: string;
};

type Batch = { batch_id: string; batch_name: string };
type CandidateStudent = {
  stuId: string;
  stuname?: string;
  email?: string;
};

type ManageSessionStudentsModalProps = {
  open: boolean;
  session: Session | null;
  onClose: () => void;
  onChanged?: () => void;
  /** 0 = Invited, 1 = Add students */
  initialTab?: 0 | 1;
};

function mergeCandidates(rows: CandidateStudent[]): CandidateStudent[] {
  const byId = new Map<string, CandidateStudent>();
  for (const row of rows) {
    const id = String(row?.stuId || '').trim();
    if (!id || byId.has(id)) continue;
    byId.set(id, row);
  }
  return Array.from(byId.values());
}

const ManageSessionStudentsModal: React.FC<ManageSessionStudentsModalProps> = ({
  open,
  session,
  onClose,
  onChanged,
  initialTab = 0,
}) => {
  const [tab, setTab] = useState(initialTab);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [invited, setInvited] = useState<InvitedStudent[]>([]);
  const [selectedInvited, setSelectedInvited] = useState<string[]>([]);
  const [courseId, setCourseId] = useState('');
  const [batchIds, setBatchIds] = useState<string[]>([]);

  const [batches, setBatches] = useState<Batch[]>([]);
  const [loadingBatches, setLoadingBatches] = useState(false);
  const [addBatchIds, setAddBatchIds] = useState<string[]>([]);
  const [candidates, setCandidates] = useState<CandidateStudent[]>([]);
  const [selectedCandidates, setSelectedCandidates] = useState<string[]>([]);
  const [loadingCandidates, setLoadingCandidates] = useState(false);

  const sessionId = session ? String(session.id) : '';
  const optional = isOptionalSession(session);
  const invitedIdSet = useMemo(
    () => new Set(invited.map((s) => s.student_id)),
    [invited],
  );

  const loadRoster = useCallback(async () => {
    if (!sessionId) return;
    setLoading(true);
    setError(null);
    try {
      const response = await authService.get(
        `/session/codelive/students/?session_id=${encodeURIComponent(sessionId)}`,
      );
      const data = response?.data || {};
      const rows: InvitedStudent[] = Array.isArray(data.students)
        ? data.students.map((s: any) => ({
            student_id: String(s.student_id || '').trim(),
            name: String(s.name || s.student_id || '').trim(),
            email: String(s.email || '').trim(),
          }))
        : [];
      setInvited(rows.filter((s) => Boolean(s.student_id)));
      setSelectedInvited([]);

      const resolvedCourse =
        String(data.course_id || '').trim() || asIdList(session?.course_id)[0] || '';
      const resolvedBatches =
        Array.isArray(data.batch_ids) && data.batch_ids.length > 0
          ? data.batch_ids.map((id: string) => String(id).trim()).filter(Boolean)
          : asIdList(session?.batch_id);
      setCourseId(resolvedCourse);
      setBatchIds(resolvedBatches);
      setAddBatchIds(resolvedBatches);
    } catch (err: any) {
      console.error(err);
      setError(
        err?.response?.data?.detail ||
          err?.message ||
          'Failed to load session students',
      );
      setInvited([]);
    } finally {
      setLoading(false);
    }
  }, [sessionId, session?.course_id, session?.batch_id]);

  useEffect(() => {
    if (!open || !session) return;
    setTab(initialTab);
    setError(null);
    setSelectedCandidates([]);
    void loadRoster();
  }, [open, session, loadRoster, initialTab]);

  useEffect(() => {
    if (!open || !courseId) {
      setBatches([]);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoadingBatches(true);
      try {
        const data = await fetchBatchesByCourseSingle(courseId);
        if (!cancelled) setBatches(data || []);
      } catch (err) {
        console.error(err);
        if (!cancelled) setBatches([]);
      } finally {
        if (!cancelled) setLoadingBatches(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, courseId]);

  const addBatchKey = addBatchIds.join(',');
  useEffect(() => {
    if (!open || tab !== 1 || !courseId || addBatchIds.length === 0) {
      setCandidates([]);
      setSelectedCandidates([]);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoadingCandidates(true);
      try {
        const results = await Promise.all(
          addBatchIds.map(async (batchId) => {
            const url = `/student/getStudents/session-filtered/?course_id=${encodeURIComponent(
              courseId,
            )}&batch_id=${encodeURIComponent(batchId)}`;
            const response = await authService.get(url);
            if (Array.isArray(response?.data)) return response.data as CandidateStudent[];
            if (Array.isArray(response?.data?.students)) {
              return response.data.students as CandidateStudent[];
            }
            return [] as CandidateStudent[];
          }),
        );
        if (!cancelled) {
          const merged = mergeCandidates(results.flat()).filter(
            (s) => !invitedIdSet.has(String(s.stuId)),
          );
          setCandidates(merged);
          setSelectedCandidates([]);
        }
      } catch (err) {
        console.error(err);
        if (!cancelled) {
          setCandidates([]);
          setSelectedCandidates([]);
        }
      } finally {
        if (!cancelled) setLoadingCandidates(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, tab, courseId, addBatchKey, invitedIdSet]);

  const batchLabelById = useMemo(
    () => new Map(batches.map((b) => [b.batch_id, b.batch_name])),
    [batches],
  );

  const allInvitedSelected =
    invited.length > 0 && invited.every((s) => selectedInvited.includes(s.student_id));
  const allCandidatesSelected =
    candidates.length > 0 &&
    candidates.every((s) => selectedCandidates.includes(String(s.stuId)));

  const handleAddBatchChange = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value;
    setAddBatchIds(typeof value === 'string' ? value.split(',') : value);
  };

  const toggleInvited = (id: string) => {
    setSelectedInvited((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const toggleCandidate = (id: string) => {
    setSelectedCandidates((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const handleRemove = async () => {
    if (!sessionId || selectedInvited.length === 0) return;
    setSaving(true);
    setError(null);
    try {
      const response = await authService.put('/session/codelive/remove-students/', {
        session_id: sessionId,
        student_ids: selectedInvited,
      });
      if (!response?.data?.success) {
        setError(response?.data?.message || 'Failed to remove students');
        return;
      }
      await loadRoster();
      onChanged?.();
    } catch (err: any) {
      console.error(err);
      setError(
        err?.response?.data?.detail || err?.message || 'Failed to remove students',
      );
    } finally {
      setSaving(false);
    }
  };

  const handleAdd = async () => {
    if (!sessionId || selectedCandidates.length === 0) return;
    setSaving(true);
    setError(null);
    try {
      const selectedStudents = candidates.filter((s) =>
        selectedCandidates.includes(String(s.stuId)),
      );
      const response = await authService.post('/session/add-students/', {
        session_id: sessionId,
        Students: selectedStudents,
      });
      if (response?.data?.success === false) {
        setError(response?.data?.message || 'Failed to add students');
        return;
      }
      await loadRoster();
      setTab(0);
      onChanged?.();
    } catch (err: any) {
      console.error(err);
      setError(
        err?.response?.data?.detail || err?.message || 'Failed to add students',
      );
    } finally {
      setSaving(false);
    }
  };

  if (!optional) return null;

  return (
    <Dialog
      open={open}
      onClose={() => !saving && onClose()}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle>Manage students</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pt: 1 }}>
        <Box>
          <Typography sx={{ fontWeight: 600, fontSize: 14 }}>
            {session?.Session_Topic || 'Session'}
          </Typography>
          <Typography sx={{ color: '#64748b', fontSize: 12 }}>
            Optional · {invited.length} invited
            {batchIds.length > 0
              ? ` · ${batchIds.length} batch${batchIds.length === 1 ? '' : 'es'}`
              : ''}
          </Typography>
        </Box>

        <Tabs
          value={tab}
          onChange={(_, next) => setTab(next)}
          sx={{ borderBottom: '1px solid #e5e7eb', minHeight: 40 }}
        >
          <Tab label="Invited" sx={{ textTransform: 'none', minHeight: 40 }} />
          <Tab label="Add students" sx={{ textTransform: 'none', minHeight: 40 }} />
        </Tabs>

        {loading ? (
          <Box sx={{ py: 4, display: 'grid', placeItems: 'center' }}>
            <PulseLoader color="#0d6efd" size={8} />
          </Box>
        ) : tab === 0 ? (
          <Box
            sx={{
              border: '1px solid #e5e7eb',
              borderRadius: 1.5,
              maxHeight: 340,
              overflow: 'auto',
            }}
          >
            {invited.length === 0 ? (
              <Typography sx={{ p: 2, color: '#64748b', fontSize: 13 }}>
                No students invited yet. Use Add students to invite.
              </Typography>
            ) : (
              <>
                <Box
                  sx={{
                    px: 1.5,
                    py: 0.5,
                    borderBottom: '1px solid #e5e7eb',
                    bgcolor: '#f8fafc',
                  }}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={allInvitedSelected}
                        indeterminate={
                          selectedInvited.length > 0 && !allInvitedSelected
                        }
                        onChange={() =>
                          setSelectedInvited(
                            allInvitedSelected
                              ? []
                              : invited.map((s) => s.student_id),
                          )
                        }
                      />
                    }
                    label={
                      <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                        Select all ({selectedInvited.length}/{invited.length})
                      </Typography>
                    }
                  />
                </Box>
                <List dense disablePadding>
                  {invited.map((student) => {
                    const checked = selectedInvited.includes(student.student_id);
                    return (
                      <ListItem key={student.student_id} disablePadding>
                        <ListItemButton
                          onClick={() => toggleInvited(student.student_id)}
                          dense
                        >
                          <ListItemIcon sx={{ minWidth: 36 }}>
                            <Checkbox
                              edge="start"
                              size="small"
                              checked={checked}
                              tabIndex={-1}
                              disableRipple
                            />
                          </ListItemIcon>
                          <ListItemText
                            primary={student.name}
                            secondary={student.email || undefined}
                            primaryTypographyProps={{ fontSize: 13, fontWeight: 500 }}
                            secondaryTypographyProps={{ fontSize: 11 }}
                          />
                        </ListItemButton>
                      </ListItem>
                    );
                  })}
                </List>
              </>
            )}
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {!courseId ? (
              <Typography color="error" sx={{ fontSize: 13 }}>
                This session has no course. Students cannot be added.
              </Typography>
            ) : loadingBatches ? (
              <Box sx={{ py: 2, display: 'grid', placeItems: 'center' }}>
                <PulseLoader color="#0d6efd" size={8} />
              </Box>
            ) : (
              <FormControl fullWidth size="small">
                <InputLabel id="manage-add-batch-label">Batches</InputLabel>
                <Select
                  labelId="manage-add-batch-label"
                  multiple
                  value={addBatchIds}
                  onChange={handleAddBatchChange}
                  input={<OutlinedInput label="Batches" />}
                  renderValue={(selected) =>
                    selected
                      .map((id) => batchLabelById.get(id) || id)
                      .join(', ')
                  }
                >
                  {batches.map((b) => (
                    <MenuItem key={b.batch_id} value={b.batch_id}>
                      <Checkbox
                        size="small"
                        checked={addBatchIds.includes(b.batch_id)}
                      />
                      <ListItemText primary={b.batch_name} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            <Box
              sx={{
                border: '1px solid #e5e7eb',
                borderRadius: 1.5,
                maxHeight: 280,
                overflow: 'auto',
              }}
            >
              {loadingCandidates ? (
                <Box sx={{ py: 3, display: 'grid', placeItems: 'center' }}>
                  <PulseLoader color="#0d6efd" size={8} />
                </Box>
              ) : addBatchIds.length === 0 ? (
                <Typography sx={{ p: 2, color: '#64748b', fontSize: 13 }}>
                  Select one or more batches to list available students.
                </Typography>
              ) : candidates.length === 0 ? (
                <Typography sx={{ p: 2, color: '#64748b', fontSize: 13 }}>
                  No additional students found in the selected batches.
                </Typography>
              ) : (
                <>
                  <Box
                    sx={{
                      px: 1.5,
                      py: 0.5,
                      borderBottom: '1px solid #e5e7eb',
                      bgcolor: '#f8fafc',
                    }}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          size="small"
                          checked={allCandidatesSelected}
                          indeterminate={
                            selectedCandidates.length > 0 && !allCandidatesSelected
                          }
                          onChange={() =>
                            setSelectedCandidates(
                              allCandidatesSelected
                                ? []
                                : candidates.map((s) => String(s.stuId)),
                            )
                          }
                        />
                      }
                      label={
                        <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                          Select all ({selectedCandidates.length}/{candidates.length})
                        </Typography>
                      }
                    />
                  </Box>
                  <List dense disablePadding>
                    {candidates.map((student) => {
                      const id = String(student.stuId);
                      const checked = selectedCandidates.includes(id);
                      return (
                        <ListItem key={id} disablePadding>
                          <ListItemButton onClick={() => toggleCandidate(id)} dense>
                            <ListItemIcon sx={{ minWidth: 36 }}>
                              <Checkbox
                                edge="start"
                                size="small"
                                checked={checked}
                                tabIndex={-1}
                                disableRipple
                              />
                            </ListItemIcon>
                            <ListItemText
                              primary={student.stuname || id}
                              secondary={student.email || undefined}
                              primaryTypographyProps={{ fontSize: 13, fontWeight: 500 }}
                              secondaryTypographyProps={{ fontSize: 11 }}
                            />
                          </ListItemButton>
                        </ListItem>
                      );
                    })}
                  </List>
                </>
              )}
            </Box>
          </Box>
        )}

        {error && (
          <Typography color="error" sx={{ fontSize: 13 }}>
            {error}
          </Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={onClose}
          disabled={saving}
          sx={{ textTransform: 'none' }}
        >
          Close
        </Button>
        {tab === 0 ? (
          <Button
            variant="contained"
            color="error"
            onClick={() => void handleRemove()}
            disabled={saving || selectedInvited.length === 0}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            {saving
              ? 'Removing…'
              : `Remove ${selectedInvited.length} student${
                  selectedInvited.length === 1 ? '' : 's'
                }`}
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={() => void handleAdd()}
            disabled={saving || selectedCandidates.length === 0}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            {saving
              ? 'Adding…'
              : `Add ${selectedCandidates.length} student${
                  selectedCandidates.length === 1 ? '' : 's'
                }`}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ManageSessionStudentsModal;
